import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, MapPin, Phone, ShieldCheck, Star, Truck } from "lucide-react";
import api from "@/lib/api";
import { BookingWidget } from "@/components/BookingWidget";
import { Reveal } from "@/components/Reveal";

export default function ListingDetailPage() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [availability, setAvailability] = useState({ booked: [] });
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setListing(null);
    setMissing(false);
    Promise.all([
      api.get(`/listings/${id}`),
      api.get(`/listings/${id}/availability`).catch(() => ({ data: { booked: [] } })),
    ])
      .then(([l, a]) => {
        setListing(l.data);
        setAvailability(a.data || { booked: [] });
      })
      .catch(() => setMissing(true));
  }, [id]);

  if (missing)
    return (
      <main className="mx-auto max-w-7xl px-4 py-32 text-center sm:px-8">
        <p className="font-display text-3xl font-extrabold text-ink">That package just rolled off the truck.</p>
        <Link to="/catalog" className="mt-6 inline-block rounded-full bg-royal px-6 py-3 text-sm font-semibold text-white">
          Back to catalogue
        </Link>
      </main>
    );

  if (!listing)
    return (
      <main className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="h-[440px] animate-pulse rounded-3xl bg-slate-200/60 lg:col-span-7" />
          <div className="h-[440px] animate-pulse rounded-3xl bg-slate-200/60 lg:col-span-5" />
        </div>
      </main>
    );

  return (
    <main className="bg-paper pb-28" data-testid="listing-detail">
      <div className="mx-auto max-w-7xl px-4 pt-10 sm:px-8 lg:px-16">
        <nav className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
          <Link to="/catalog" data-testid="breadcrumb-catalog-link" className="transition-colors duration-200 hover:text-royal">
            Catalogue
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-royal">{listing.category}</span>
        </nav>
        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl shadow-2xl shadow-slate-900/10">
                <img
                  src={listing.image}
                  alt={listing.title}
                  data-testid="listing-image"
                  className="h-[320px] w-full object-cover sm:h-[460px]"
                />
                <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-white/90 px-3.5 py-1.5 backdrop-blur">
                  <Star className="h-3.5 w-3.5 fill-gold text-gold" />
                  <span className="text-xs font-bold text-ink">{listing.rating}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    sanitized
                  </span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-9 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
                {listing.title}
              </h1>
              <p className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-royal">{listing.brand}</span>
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {listing.location}</span>
              </p>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600">
                {listing.description}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <h2 className="mt-12 font-display text-xl font-bold text-ink">Package details</h2>
              <dl className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="specs-table">
                {Object.entries(listing.specs || {}).map(([k, v], i) => (
                  <div key={k} className={`grid grid-cols-2 gap-4 px-6 py-4 ${i % 2 ? "bg-paper" : "bg-white"}`}>
                    <dt className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{k}</dt>
                    <dd className="text-sm font-medium text-ink">{v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-royal" />
                  <div>
                    <p className="text-sm font-bold text-ink">Damage waiver</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                      Scuffs, cracks and lid swaps are covered for the whole rental window.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                  <Truck className="mt-0.5 h-5 w-5 shrink-0 text-royal" />
                  <div>
                    <p className="text-sm font-bold text-ink">Door delivery</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                      Free delivery & pickup across Bend, Redmond, Sisters and Sunriver on orders over $25.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal delay={0.1} y={20}>
                {listing.billing === "call" ? (
                  <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-900/5" data-testid="call-pricing-card">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-royal">
                      Custom quote
                    </p>
                    <p className="mt-3 font-display text-3xl font-extrabold text-ink">Call for pricing</p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">
                      Orders above 75 totes are priced custom — multi-week discounts, staged delivery and
                      dolly fleet pricing included. Tell us the home size and timeline and we'll quote it the same day.
                    </p>
                    <a
                      href="tel:+15414087019"
                      data-testid="call-pricing-button"
                      className="mt-6 flex h-13 w-full items-center justify-center gap-2 rounded-xl bg-royal py-4 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.01] hover:bg-royal-dark hover:shadow-lg hover:shadow-royal/25 active:scale-[0.98]"
                    >
                      <Phone className="h-4 w-4" /> (541) 408-7019
                    </a>
                    <p className="mt-3.5 text-center text-xs text-slate-500">
                      or email reservations@oregon-tote-rentals.com — we reply within business hours
                    </p>
                  </div>
                ) : (
                  <BookingWidget listing={listing} booked={availability.booked || []} inventory={availability.inventory} packageQty={availability.tote_qty || 0} />
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
